"use client";

import React from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { EcommerceTermsProvider, useEcommerceTerms } from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";
const useWvcLanguage = () => {
  const [lang, setLang] = React.useState("en");
  React.useEffect(() => {
    const update = () => {
      const docLang = document.documentElement.lang || "en";
      const normalized = docLang.slice(0, 2).toLowerCase();
      setLang(["en", "zh", "fr", "es", "de"].includes(normalized) ? normalized : "en");
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["lang"]
    });
    return () => observer.disconnect();
  }, []);
  return lang as "en" | "zh" | "fr" | "es" | "de";
};
const translations = {
  en: {
    collections: "Collections",
    curated: "Curated portals",
    viewAll: "View all categories",
    error: "Unable to display category collections right now.",
    retry: "Try again",
    piece: "piece",
    pieces: "pieces",
    dressesName: "Dresses",
    dressesDesc: "Tailored daytime wear and fluid evening silhouettes.",
    arrivalsName: "New Arrivals",
    arrivalsDesc: "The latest seasonal cuts, natural weaves, and coats.",
    resortName: "Resort Edit",
    resortDesc: "Breathable linens and airy draping for warmer days.",
    travelName: "Travel Styling",
    travelDesc: "Crease-resistant essentials built to move with you.",
    essentialsName: "Daily Essentials",
    essentialsDesc: "Crisp shirting, relaxed trousers, and fine knit tops.",
    saleName: "Sale",
    saleDesc: "Archival pieces and seasonal reductions."
  },
  zh: {
    collections: "系列",
    curated: "精心策划的门户",
    viewAll: "查看所有类别",
    error: "目前无法显示类别集合。",
    retry: "再试一次",
    piece: "件",
    pieces: "件",
    dressesName: "连衣裙",
    dressesDesc: "裁剪日装和流畅的晚礼服剪裁。",
    arrivalsName: "新品上市",
    arrivalsDesc: "最新季节性剪裁、天然织物和外套。",
    resortName: "度假编辑",
    resortDesc: "透气亚麻和温暖日子的通风褶皱。",
    travelName: "旅行造型",
    travelDesc: "抗皱必需品，可随您移动。",
    essentialsName: "日常必需品",
    essentialsDesc: "清爽衬衫、轻松裤子和精细针织衫。",
    saleName: "销售",
    saleDesc: "存档件和季节性折扣。"
  },
  fr: {
    collections: "Collections",
    curated: "Portails Curés",
    viewAll: "Voir toutes les catégories",
    error: "Impossible d'afficher les collections de catégories en ce moment.",
    retry: "Réessayer",
    piece: "pièce",
    pieces: "pièces",
    dressesName: "Robes",
    dressesDesc: "Tenue de jour taillée et silhouettes de soirée fluides.",
    arrivalsName: "Nouveautés",
    arrivalsDesc: "Les derniers coupes saisonnières, tissus naturels et manteaux.",
    resortName: "Édition Resort",
    resortDesc: "Lins respirants et drapés aérés pour les jours chauds.",
    travelName: "Style Voyage",
    travelDesc: "Essentiels anti-plis conçus pour bouger avec vous.",
    essentialsName: "Essentiels Quotidiens",
    essentialsDesc: "Chemises croustillantes, pantalons décontractés et hauts en tricot fin.",
    saleName: "Solde",
    saleDesc: "Pièces d'archives et réductions saisonnières."
  },
  es: {
    collections: "Colecciones",
    curated: "Portales Curados",
    viewAll: "Ver todas las categorías",
    error: "No se pueden mostrar las colecciones de categorías en este momento.",
    retry: "Intentar de Nuevo",
    piece: "pieza",
    pieces: "piezas",
    dressesName: "Vestidos",
    dressesDesc: "Ropa de día ajustada y siluetas fluidas de noche.",
    arrivalsName: "Nuevas Llegadas",
    arrivalsDesc: "Los últimos cortes estacionales, tejidos naturales y abrigos.",
    resortName: "Edición Resort",
    resortDesc: "Linos transpirables y drapeos aéreos para días más cálidos.",
    travelName: "Estilo de Viaje",
    travelDesc: "Artículos esenciales resistentes a las arrugas diseñados para moverse contigo.",
    essentialsName: "Artículos Esenciales Diarios",
    essentialsDesc: "Camisas crujientes, pantalones relajados y tops de punto fino.",
    saleName: "Venta",
    saleDesc: "Piezas de archivo y reducciones estacionales."
  },
  de: {
    collections: "Kollektionen",
    curated: "Kuratierte Portale",
    viewAll: "Alle Kategorien anzeigen",
    error: "Kategoriesammlungen können derzeit nicht angezeigt werden.",
    retry: "Erneut Versuchen",
    piece: "Teil",
    pieces: "Teile",
    dressesName: "Kleider",
    dressesDesc: "Maßgeschneiderte Tageskleidung und fließende Abendsilhouetten.",
    arrivalsName: "Neue Ankünfte",
    arrivalsDesc: "Neueste saisonale Schnitte, natürliche Webereien und Mäntel.",
    resortName: "Resort-Ausgabe",
    resortDesc: "Atmungsaktive Leinen und luftige Drapierungen für wärmere Tage.",
    travelName: "Reisestil",
    travelDesc: "Faltenresistente Essentials, die sich mit Ihnen bewegen.",
    essentialsName: "Tägliche Essentials",
    essentialsDesc: "Knackige Hemden, lockere Hosen und feine Stricktops.",
    saleName: "Verkauf",
    saleDesc: "Archivstücke und saisonale Reduktionen."
  }
};
const getFallbackCategories = (t: (typeof translations)["en"]) => [{
  name: t.dressesName,
  description: t.dressesDesc,
  count: 24,
  link: "/ShopPage",
  image_url: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/category_dresses.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/category_dresses.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/category_dresses.webp-wvc-sizes"
}, {
  name: t.arrivalsName,
  description: t.arrivalsDesc,
  count: 18,
  link: "/ShopPage",
  image_url: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/category_new_arrivals.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/category_new_arrivals.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/category_new_arrivals.png-wvc-sizes"
}, {
  name: t.resortName,
  description: t.resortDesc,
  count: 15,
  link: "/ShopPage",
  image_url: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/category_resort.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/category_resort.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/category_resort.webp-wvc-sizes"
}, {
  name: t.travelName,
  description: t.travelDesc,
  count: 12,
  link: "/ShopPage",
  image_url: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/category_travel.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/category_travel.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/category_travel.png-wvc-sizes"
}, {
  name: t.essentialsName,
  description: t.essentialsDesc,
  count: 32,
  link: "/ShopPage",
  image_url: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/category_essentials.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/category_essentials.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/category_essentials.png-wvc-sizes"
}, {
  name: t.saleName,
  description: t.saleDesc,
  count: 14,
  link: "/ShopPage",
  image_url: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/category_sale.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/category_sale.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/category_sale.webp-wvc-sizes"
}];
function CategoriesConsumer() {
  const language = useWvcLanguage();
  const t = translations[language];
  const fallbackCategories = getFallbackCategories(t);
  const {
    terms,
    loading,
    error,
    refetch
  } = useEcommerceTerms();
  if (loading) {
    return <div className="grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 md:gap-7">
        {[0, 1, 2, 3, 4, 5].map((_, i) => <div key={i} data-index={i} className="flex flex-col border border-border bg-card p-2">
            <div className="aspect-3/4 w-full animate-pulse bg-muted" />
            <div className="mt-4 flex items-center justify-between px-2 pb-2">
              <div className="h-4 w-24 animate-pulse bg-muted" />
              <div className="h-3 w-8 animate-pulse bg-muted" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center border border-border bg-card p-10 text-center">
        <p className="text-sm text-muted-foreground">
          {t.error}
        </p>
        <div className="mt-4">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            {t.retry}
          </Button>
        </div>
      </div>;
  }
  const displayList = terms && terms.length > 0 ? terms.slice(0, 6).map((term, i) => ({
    name: term.name,
    description: term.description || "",
    count: term.count,
    link: term.link || "/ShopPage",
    image_url: term.image_url || fallbackCategories[i % fallbackCategories.length].image_url,
    isDynamic: true
  })) : fallbackCategories.map(item => ({
    ...item,
    isDynamic: false
  }));
  return <div className="grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 md:gap-7">
      {displayList.map((category, i) => <Link key={category.name + i} to={category.link} data-index={i} className="group relative flex flex-col overflow-hidden border border-border bg-card transition-colors duration-200 hover:border-foreground/40">
          <div className="relative aspect-3/4 w-full overflow-hidden bg-muted">
            {category.isDynamic ? <img src={category.image_url} alt={category.name} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-105" /> : <img src={category.image_url} data-wvc-srcset={category.srcset} data-wvc-sizes={category.sizes} alt={category.name} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-105" />}

            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-80 transition-opacity duration-200 group-hover:opacity-90" />

            <div className="absolute inset-x-0 bottom-0 flex flex-col justify-end p-4 text-background sm:p-5">
              <div className="flex items-end justify-between gap-2">
                <div>
                  <h3 className="font-serif text-lg leading-tight tracking-tight sm:text-xl md:text-2xl">
                    {category.name}
                  </h3>
                  {category.count !== undefined && <span className="mt-1 block text-xs tracking-wider text-background/80">
                      {category.count} {category.count === 1 ? t.piece : t.pieces}
                    </span>}
                </div>
                <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-background/20 backdrop-blur-xs transition-colors duration-200 group-hover:bg-background group-hover:text-foreground">
                  <ArrowUpRight className="size-4" />
                </div>
              </div>
            </div>
          </div>
        </Link>)}
    </div>;
}
const termQueryParams = {
  taxonomy: "product_cat",
  per_page: 6,
  order: "asc",
  orderby: "name"
};
const termQuery = new WP_Term_Query(termQueryParams);
export default function Categories() {
  const language = useWvcLanguage();
  const t = translations[language];
  return <section id="分类入口" data-nav="light" className="bg-background text-foreground py-14 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col justify-between border-b border-border pb-6 sm:mb-12 sm:flex-row sm:items-end">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {t.collections}
            </span>
            <h2 className="mt-1 font-serif text-2xl tracking-tight sm:text-3xl md:text-4xl">
              {t.curated}
            </h2>
          </div>
          <div className="mt-4 sm:mt-0">
            <Button variant="link" asChild className="px-0 text-foreground hover:text-primary">
              <Link to="/ShopPage">
                {t.viewAll}
                <ArrowUpRight className="ml-1 size-4" />
              </Link>
            </Button>
          </div>
        </div>

        <EcommerceTermsProvider wp_term_query={{
        taxonomy: "product_cat",
        per_page: 6,
        order: "asc",
        orderby: "name"
      }}>
          <CategoriesConsumer />
        </EcommerceTermsProvider>
      </div>
    </section>;
}